/**
 * Admin Center catalog: one list of every integration and every settings link, shared by the Admin Center hub
 * (/admin), the Integrations page and the settings search. `cap` is a UI convenience only; the API enforces access.
 */
import type { ConsoleCapability } from "@flexfreight/core";
import type { PillTone } from "@flexfreight/ui";

export type IntegrationCategory = "Zoho" | "Support" | "Billing" | "Email & text" | "AI" | "Carriers" | "Maps" | "Security";
export const INTEGRATION_CATEGORIES: readonly IntegrationCategory[] = ["Zoho", "Support", "Billing", "Email & text", "AI", "Carriers", "Maps", "Security"];

export type IntegrationId = "zoho" | "zoho-desk" | "zoho-books" | "m365" | "resend" | "twilio" | "anthropic" | "google-maps" | "eagleload" | "signin-policy";

export type IntegrationEntry = {
  id: IntegrationId;
  name: string;
  blurb: string;
  category: IntegrationCategory;
  mark: string;
  /** Same query key and URL the integration's own card uses, so the tile and the pop-up share one cached answer. */
  queryKey: readonly string[];
  url: string;
  keywords: string;
};

export const INTEGRATIONS: readonly IntegrationEntry[] = [
  { id: "zoho", name: "Zoho connection", blurb: "One sign-in shared by Zoho Desk and Zoho Books", category: "Zoho", mark: "Zo", queryKey: ["console", "integrations", "zoho"], url: "/v1/console/integrations/zoho", keywords: "zoho oauth client authorization code connect refresh token" },
  { id: "zoho-desk", name: "Zoho Desk", blurb: "Tickets, customer messages and the knowledge base", category: "Support", mark: "ZD", queryKey: ["console", "integrations", "zoho-desk"], url: "/v1/console/integrations/zoho-desk", keywords: "tickets help center departments sync" },
  { id: "zoho-books", name: "Zoho Books", blurb: "Invoices for every booking, emailed to the customer", category: "Billing", mark: "ZB", queryKey: ["console", "integrations", "zoho-books"], url: "/v1/console/integrations/zoho-books", keywords: "invoices billing accounting auto-invoice net 15" },
  { id: "m365", name: "Microsoft 365 mail", blurb: "FlexFreight mailboxes for the inbox and staff email", category: "Email & text", mark: "M3", queryKey: ["console", "integrations", "m365"], url: "/v1/console/integrations/m365", keywords: "outlook email inbox mailbox tenant" },
  { id: "resend", name: "Resend", blurb: "Sign-in codes, invites and system email", category: "Email & text", mark: "Rs", queryKey: ["console", "integrations", "resend"], url: "/v1/console/integrations/resend", keywords: "email codes invites password reset" },
  { id: "twilio", name: "Twilio", blurb: "Sign-in codes by text message", category: "Email & text", mark: "Tw", queryKey: ["console", "integrations", "twilio"], url: "/v1/console/integrations/twilio", keywords: "sms text phone codes" },
  { id: "anthropic", name: "Flex AI", blurb: "The Anthropic key that runs Flex AI", category: "AI", mark: "AI", queryKey: ["console", "ai"], url: "/v1/console/ai", keywords: "anthropic claude assistant key" },
  { id: "eagleload", name: "EagleLoad", blurb: "Live LTL carrier rates and booking", category: "Carriers", mark: "EL", queryKey: ["console", "integrations", "eagleload"], url: "/v1/console/integrations/eagleload", keywords: "rates quotes ltl carriers api key" },
  { id: "google-maps", name: "Google Maps", blurb: "Address autocomplete for quotes and bookings", category: "Maps", mark: "GM", queryKey: ["console", "integrations", "google-maps"], url: "/v1/console/integrations/google-maps", keywords: "address autocomplete places zip" },
  { id: "signin-policy", name: "Two-step sign-in", blurb: "Require a second step for owners and admins", category: "Security", mark: "2S", queryKey: ["console", "auth-policy"], url: "/v1/console/auth-policy", keywords: "security mfa authenticator policy" },
];

export const COMING_SOON: readonly { name: string; blurb: string; category: IntegrationCategory; mark: string }[] = [
  { name: "Outbound email", blurb: "Send from FlexFreight mailboxes once approved", category: "Email & text", mark: "OE" },
  { name: "Marketing sending", blurb: "Campaign email and SMS, off by default", category: "Email & text", mark: "MS" },
];

export type TileStatus = { label: string; tone: PillTone; state: "connected" | "not-set-up" | "attention" };

/** One plain status per integration, read from the same data its card shows. */
export function tileStatus(id: IntegrationId, data: unknown): TileStatus | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  if (id === "anthropic") return d.mode === "live" ? { label: "Connected", tone: "success", state: "connected" } : { label: "Not set up", tone: "neutral", state: "not-set-up" };
  if (id === "zoho-books" && typeof d.status === "string" && /^(auto-invoicing|connected)/i.test(d.status)) return { label: d.status, tone: "success", state: "connected" };
  if (id === "signin-policy") return d.requirePrivilegedTwoStep ? { label: "Required", tone: "success", state: "connected" } : { label: "Off", tone: "warning", state: "attention" };
  const raw = typeof d.status === "string" ? d.status : "";
  if (/^(connected|syncing|on\b|live)/i.test(raw)) return { label: "Connected", tone: "success", state: "connected" };
  if (!raw || /^awaiting/i.test(raw)) return { label: "Not set up", tone: "neutral", state: "not-set-up" };
  return { label: raw, tone: /ready/i.test(raw) ? "info" : "warning", state: "attention" };
}

export type SettingLink = { label: string; to: string; cap: ConsoleCapability; keywords?: string };
export type SettingGroup = { id: string; label: string; links: SettingLink[] };

const int = (id: IntegrationId) => `/admin/integrations?open=${id}`;

/** The Admin Center hub: every setting, grouped the way staff look for it. */
export const SETTING_GROUPS: readonly SettingGroup[] = [
  {
    id: "carriers",
    label: "Carriers",
    links: [
      { label: "Carriers & API keys", to: "/admin/carriers", cap: "carriers:manage", keywords: "odfl xpo estes saia credentials" },
      { label: "EagleLoad rates", to: int("eagleload"), cap: "integrations:manage", keywords: "ltl rates booking" },
      { label: "Carrier health", to: "/carrier-health", cap: "carriers:read" },
    ],
  },
  {
    id: "people",
    label: "People",
    links: [
      { label: "Team & permissions", to: "/admin/team", cap: "staff:manage", keywords: "staff roles invite users" },
      { label: "Two-step sign-in", to: int("signin-policy"), cap: "integrations:manage", keywords: "security mfa authenticator" },
    ],
  },
  {
    id: "integrations",
    label: "Integrations",
    links: [
      { label: "All integrations", to: "/admin/integrations", cap: "integrations:manage" },
      { label: "Zoho connection", to: int("zoho"), cap: "integrations:manage", keywords: "zoho oauth code" },
      { label: "Zoho Desk", to: int("zoho-desk"), cap: "integrations:manage", keywords: "tickets help center" },
      { label: "Zoho Books", to: int("zoho-books"), cap: "integrations:manage", keywords: "invoices billing" },
      { label: "Microsoft 365 mail", to: int("m365"), cap: "integrations:manage", keywords: "outlook mailboxes" },
      { label: "Google Maps", to: int("google-maps"), cap: "integrations:manage", keywords: "address autocomplete" },
    ],
  },
  {
    id: "messaging",
    label: "Email & text",
    links: [
      { label: "Resend email", to: int("resend"), cap: "integrations:manage", keywords: "sign-in codes invites" },
      { label: "Twilio text messages", to: int("twilio"), cap: "integrations:manage", keywords: "sms codes" },
      { label: "Email templates", to: "/inbox/templates", cap: "email:read", keywords: "replies signatures" },
    ],
  },
  {
    id: "ai",
    label: "Flex AI",
    links: [
      { label: "Flex AI connection", to: int("anthropic"), cap: "integrations:manage", keywords: "anthropic key" },
      { label: "Ask Flex AI", to: "/ai", cap: "ai:use", keywords: "assistant chat" },
    ],
  },
  {
    id: "company",
    label: "Company",
    links: [{ label: "Company & policies", to: "/admin/company", cap: "settings:read", keywords: "markup terms billing policy invoice" }],
  },
  {
    id: "monitoring",
    label: "Monitoring",
    links: [
      { label: "Audit log", to: "/admin/audit", cap: "audit:read", keywords: "history changes" },
      { label: "Problem reports", to: "/admin/problems", cap: "settings:read", keywords: "bugs issues" },
      { label: "System health", to: "/admin/health", cap: "settings:read", keywords: "status uptime" },
    ],
  },
];

/** Groups and links the given capabilities allow, narrowed by a search term (label, keywords or group name). */
export function settingGroupsFor(caps: readonly string[], term = ""): SettingGroup[] {
  const have = new Set(caps);
  const t = term.trim().toLowerCase();
  return SETTING_GROUPS.map((g) => ({
    ...g,
    links: g.links.filter((l) => have.has(l.cap) && (!t || `${l.label} ${l.keywords ?? ""} ${g.label}`.toLowerCase().includes(t))),
  })).filter((g) => g.links.length > 0);
}
